import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Camera, Image as ImageIcon, Flame, Beef, Wheat, Droplet, Heart,
  TrendingDown, Dumbbell, Zap, Sparkles, ArrowLeftRight, History, RotateCw, X,
  Bookmark, BookmarkCheck, Trash2, Sunrise, Sun, Moon, Cookie, Info, BookOpen,
  ChevronRight, Crown, Lock,
} from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/Toast";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";
import NutritionDisclaimer from "@/components/NutritionDisclaimer";
import NutritionInfoModal from "@/components/NutritionInfoModal";
import NutritionCitation, { citationOf, textOf } from "@/components/NutritionCitation";

const MEAL_LABELS = {
  breakfast: { name: "Breakfast", Icon: Sunrise },
  lunch:     { name: "Lunch",     Icon: Sun },
  dinner:    { name: "Dinner",    Icon: Moon },
  snack:     { name: "Snacks",    Icon: Cookie },
};
const MEAL_ORDER = ["breakfast", "lunch", "dinner", "snack"];

// ------------------------------------------------------------------
// Daily tracker — 4 macro progress bars + grouped meals + history.
// Pulls /api/nutrition/today, owns refresh + meal-tap UX.
// ------------------------------------------------------------------
function MacroBar({ label, used, target, color, testId }) {
  const pct = target ? Math.min(100, Math.round((used / target) * 100)) : 0;
  return (
    <div data-testid={testId}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span className="tiny" style={{ color: "var(--text-dim)", letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 600 }}>
          {label}
        </span>
        <span className="tiny" style={{ color: "var(--text)", fontWeight: 600 }} data-testid={`${testId}-num`}>
          {Math.round(used)}{target ? ` / ${target}` : ""}
        </span>
      </div>
      <div
        style={{
          height: 8, borderRadius: 99, overflow: "hidden",
          background: "rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            height: "100%", width: `${pct}%`, background: color,
            borderRadius: 99, transition: "width 320ms ease",
          }}
        />
      </div>
    </div>
  );
}

function DailyTracker({ today, onTapMeal, onJumpHistory, testId = "daily-tracker" }) {
  if (!today) {
    return (
      <div className="card" data-testid={`${testId}-loading`} style={{ padding: 18, marginBottom: 14, color: "var(--text-faint)" }}>
        Loading your day…
      </div>
    );
  }
  const p = today.progress || {};
  const t = today.totals   || { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  const noTargets = !p.calories?.target;
  const allMeals = MEAL_ORDER.flatMap((k) => today.meals?.[k] || []);

  return (
    <div data-testid={testId}>
      {/* Headline totals card */}
      <div
        className="card"
        data-testid={`${testId}-totals`}
        style={{
          padding: 18, marginBottom: 14,
          border: "1px solid rgba(232,211,185,0.30)",
          background: "linear-gradient(180deg, rgba(232,211,185,0.06) 0%, rgba(0,0,0,0) 70%)",
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "var(--gold)" }}>TODAY</span>
          {today.date && (
            <span className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.10em" }}>
              {new Date(today.date + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
          <div className="h1" style={{ fontSize: 36, lineHeight: 1, fontWeight: 600 }} data-testid={`${testId}-kcal`}>
            {Math.round(t.calories)}
          </div>
          {p.calories?.target ? (
            <div className="body" style={{ color: "var(--text-dim)" }}>
              <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>/</em>{" "}
              {p.calories.target} kcal
            </div>
          ) : (
            <div className="body" style={{ color: "var(--text-dim)" }}>kcal today</div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <MacroBar label="Calories" used={t.calories}  target={p.calories?.target}  color="var(--gold)"            testId={`${testId}-cal`} />
          <MacroBar label="Protein"  used={t.protein_g} target={p.protein_g?.target} color="#ef6b6b"                testId={`${testId}-protein`} />
          <MacroBar label="Carbs"    used={t.carbs_g}   target={p.carbs_g?.target}   color="#5fb8c8"                testId={`${testId}-carbs`} />
          <MacroBar label="Fat"      used={t.fat_g}     target={p.fat_g?.target}     color="#c79afc"                testId={`${testId}-fat`} />
        </div>
        {noTargets && (
          <div className="tiny" style={{ marginTop: 10, color: "var(--text-faint)" }}>
            Set height &amp; weight in your Profile to see personalised targets.
          </div>
        )}
      </div>

      {/* Today's Meals grouped */}
      <div className="outfit-section" data-testid={`${testId}-meals`}>
        <div className="eyebrow" style={{ marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
          <span>TODAY&apos;S MEALS</span>
          {allMeals.length > 0 ? (
            <span className="tiny" style={{ color: "var(--text-faint)" }}>{allMeals.length} logged</span>
          ) : null}
        </div>
        {allMeals.length === 0 ? (
          <div className="card" style={{ padding: 14, color: "var(--text-faint)" }} data-testid={`${testId}-empty`}>
            No meals yet — scan one below to start tracking.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {MEAL_ORDER.map((slot) => {
              const list = today.meals?.[slot] || [];
              if (list.length === 0) return null;
              const { name, Icon } = MEAL_LABELS[slot];
              const slotCal = list.reduce((a, m) => a + (m.totals?.calories || 0), 0);
              return (
                <div key={slot} data-testid={`${testId}-slot-${slot}`}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <span className="tiny" style={{ color: "var(--text-dim)", letterSpacing: "0.10em", textTransform: "uppercase", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Icon size={11} strokeWidth={1.8} /> {name}
                    </span>
                    <span className="tiny" style={{ color: "var(--text-faint)" }}>{slotCal} kcal</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {list.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => onTapMeal(m, slot)}
                        className="card"
                        data-testid={`${testId}-meal-${m.id}`}
                        style={{
                          padding: "10px 12px", textAlign: "left", cursor: "pointer",
                          font: "inherit", color: "inherit",
                          display: "flex", alignItems: "center", gap: 10, width: "100%",
                        }}
                      >
                        <Flame size={14} color="var(--gold)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="body-sm" style={{ color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {(m.items || []).slice(0, 3).map((it) => it.name).join(", ") || m.summary || "Meal"}
                          </div>
                          <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 2 }}>
                            {m.totals?.calories || 0} kcal · {m.totals?.protein_g || 0}p · {m.totals?.carbs_g || 0}c · {m.totals?.fat_g || 0}f
                          </div>
                        </div>
                        <span className="tiny" style={{ color: "var(--text-faint)" }}>
                          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <button
          type="button"
          onClick={onJumpHistory}
          className="btn btn-outline"
          data-testid={`${testId}-history-btn`}
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
        >
          <History size={14} /> Past days
        </button>
      </div>
    </div>
  );
}

// Meal detail modal — read items, delete the meal.
function MealDetailModal({ meal, onClose, onDelete, testId = "meal-modal" }) {
  if (!meal) return null;
  return (
    <div
      data-testid={testId}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          background: "var(--bg-elev)", padding: 22,
          borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 520,
          maxHeight: "82vh", overflowY: "auto", border: "1px solid var(--line)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div className="eyebrow" style={{ color: "var(--gold)" }}>
            {MEAL_LABELS[meal.meal_type || "snack"]?.name || "Meal"}
          </div>
          <button type="button" onClick={onClose} className="icon-btn" data-testid={`${testId}-close`}>
            <X size={14} strokeWidth={1.8} />
          </button>
        </div>
        <div className="h2" style={{ fontSize: 22, marginBottom: 2 }}>
          {meal.totals?.calories || 0} kcal
        </div>
        <div className="tiny" style={{ color: "var(--text-faint)", marginBottom: 14 }}>
          {meal.totals?.protein_g || 0}g protein · {meal.totals?.carbs_g || 0}g carbs · {meal.totals?.fat_g || 0}g fat
          {meal.created_at && " · " + new Date(meal.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </div>
        {meal.items?.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }} data-testid={`${testId}-items`}>
            {meal.items.map((it, i) => (
              <div
                key={i}
                className="card"
                style={{ padding: "10px 12px", display: "flex", justifyContent: "space-between", gap: 12 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="body-sm" style={{ color: "var(--text)" }}>{it.name}</div>
                  {it.qty && <div className="tiny" style={{ color: "var(--text-faint)" }}>{it.qty}</div>}
                </div>
                <div className="tiny" style={{ color: "var(--text-dim)", textAlign: "right" }}>
                  {it.calories || 0} kcal<br />
                  <span style={{ color: "var(--text-faint)" }}>
                    {it.protein_g || 0}p · {it.carbs_g || 0}c · {it.fat_g || 0}f
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tiny" style={{ color: "var(--text-faint)", marginBottom: 16 }}>
            No itemised breakdown for this scan.
          </div>
        )}
        <button
          type="button"
          onClick={() => onDelete(meal.id)}
          className="btn btn-outline"
          data-testid={`${testId}-delete`}
          style={{ width: "100%", justifyContent: "center", color: "var(--danger)", borderColor: "rgba(255,120,120,0.3)" }}
        >
          <Trash2 size={14} /> Remove this meal from today
        </button>
      </div>
    </div>
  );
}

async function fileToResizedDataUrl(file, maxDim = 1200) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s); height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.86));
      };
      img.onerror = () => reject(new Error("decode"));
      img.src = r.result;
    };
    r.onerror = () => reject(new Error("read"));
    r.readAsDataURL(file);
  });
}

function NutritionBalance({ score }) {
  // score is always 0-100. Defensive: if a legacy value <=10 sneaks in,
  // normalise to 0-100 so the ring + numeric label stay consistent.
  const raw = typeof score === "number" ? score : Number(score) || 0;
  const normalized = raw <= 10 ? raw * 10 : raw;
  const clamped = Math.max(0, Math.min(100, Math.round(normalized)));
  // Slightly softer palette than the previous good/danger red — luxurious,
  // never medically alarming.
  const color = clamped >= 70 ? "var(--good, #b8ffd0)" : clamped >= 40 ? "var(--gold, #b28a3e)" : "#e88a8a";
  return (
    <div
      className="card"
      data-testid="nutri-nutrition-balance"
      style={{
        padding: "20px 22px",
        display: "flex",
        alignItems: "center",
        gap: 20,
        border: "1px solid rgba(178,138,62,0.20)",
      }}
    >
      <div style={{
        width: 68, height: 68, borderRadius: "50%",
        background: `conic-gradient(${color} ${clamped * 3.6}deg, rgba(255,255,255,0.06) 0)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <div style={{
          width: 54, height: 54, borderRadius: "50%", background: "var(--bg-elev, #0e0e0e)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color, lineHeight: 1 }}>{clamped}</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="tiny" style={{ letterSpacing: "0.20em", textTransform: "uppercase", color: "var(--text-faint)", fontSize: 10 }}>
          NUTRITION BALANCE
        </div>
        <div style={{ marginTop: 4, fontFamily: "'Fraunces', serif", fontSize: 20, color: "var(--text)", lineHeight: 1.15 }}>
          {clamped} <span style={{ color: "var(--text-faint)", fontSize: 15 }}>/ 100</span>
        </div>
        <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)", letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 9.5 }}>
          Estimated
        </div>
      </div>
    </div>
  );
}

// Soft verdict card — Apple-friendly wording. Never says "Yes/No"; instead
// frames the verdict as guidance about how well the meal supports a goal.
// Colour is dialed down to a neutral gold/danger tint (no red alarms).
// A tap-through citation tag sits under the reason and opens the sources
// modal.
function GoalCard({ label, icon: Icon, verdict, testId, onOpenSources }) {
  if (!verdict) return null;
  const supportive = !!verdict.good;
  const src = citationOf(verdict, "NIH");
  return (
    <div
      className="card"
      data-testid={testId}
      style={{
        padding: "14px 16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        border: `1px solid ${supportive ? "rgba(184,255,208,0.22)" : "rgba(178,138,62,0.22)"}`,
        background: supportive ? "rgba(184,255,208,0.04)" : "rgba(178,138,62,0.04)",
      }}
    >
      <div
        className="tile-icon"
        style={{
          width: 34, height: 34, flexShrink: 0,
          background: supportive ? "rgba(184,255,208,0.10)" : "rgba(178,138,62,0.12)",
        }}
      >
        <Icon size={15} color={supportive ? "var(--good, #b8ffd0)" : "var(--gold, #b28a3e)"} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "var(--text)", fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
          {label}
        </div>
        <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 5, lineHeight: 1.55 }}>
          {verdict.reason}
        </div>
        <NutritionCitation source={src} onOpenSources={onOpenSources} testId={`${testId}-cite`} />
      </div>
    </div>
  );
}

// Backwards-compat shim — some earlier reports referenced <VerdictRow /> and
// <HealthScore />. Keep the exports so imports elsewhere don't 500.
const VerdictRow = GoalCard;
const HealthScore = NutritionBalance;

function MacroTile({ icon: Icon, label, value, unit, testId }) {
  return (
    <div className="glow-sub-card" data-testid={testId}>
      <div className="glow-sub-card-icon"><Icon size={15} strokeWidth={1.8} /></div>
      <div className="glow-sub-card-label">{label}</div>
      <div className="glow-sub-card-value">{value}{unit}</div>
    </div>
  );
}

/**
 * Build a personalised, educational 2-sentence summary from the analyzer
 * result. Preferred source of truth is `result.health_score_reason` (the
 * server prompt now returns personalised text). This helper is the fallback
 * used when the analyzer omits that field — it mines `items`, `macros`, and
 * `watch_outs` to compose a sentence that mentions the actual foods and the
 * macros that stand out. Never medical, always framed as an estimate.
 */
function composePersonalisedSummary(result) {
  if (!result) return "";
  if (result.health_score_reason) return result.health_score_reason;

  const items = Array.isArray(result.items) ? result.items : [];
  const foodNames = items
    .map((i) => (typeof i === "string" ? i : i?.name || i?.label))
    .filter(Boolean)
    .slice(0, 3);
  const cal = typeof result.total_calories === "number" ? result.total_calories : null;
  const m = result.macros || {};
  const protein = Number(m.protein_g) || 0;
  const carbs = Number(m.carbs_g) || 0;
  const fats = Number(m.fats_g) || 0;
  const fiber = Number(m.fiber_g) || 0;
  const score = Number(result.health_score) || 0;

  // Descriptor words based on rough thresholds. We stay soft: "very high",
  // "high", "moderate", "low" — never clinical.
  const calorieDesc = cal == null ? null : (cal >= 900 ? "very high in calories" : cal >= 600 ? "high in calories" : cal >= 350 ? "moderate in calories" : "light in calories");
  const proteinDesc = protein >= 40 ? "high in protein" : protein >= 20 ? "moderate in protein" : "low in protein";
  const fatDesc = fats >= 35 ? "high in saturated and total fat" : fats >= 20 ? "moderate in fat" : null;
  const carbDesc = carbs >= 80 ? "high in carbohydrates" : null;
  const fiberDesc = fiber < 4 ? "low in fibre" : fiber >= 8 ? "high in fibre" : null;

  const stand = [calorieDesc, proteinDesc, fatDesc, carbDesc, fiberDesc].filter(Boolean).slice(0, 3);
  const foodPhrase = foodNames.length
    ? ` The ${foodNames.slice(0, 2).join(" and ")}${foodNames.length > 2 ? " and other items" : ""}`
    : " This meal";

  // Suggestion tailored to what stands out
  let suggestion;
  if (score < 45 && (fatDesc || carbDesc)) {
    suggestion = "Adding more vegetables and reducing fried or sugary sides may create a more balanced meal.";
  } else if (protein < 20) {
    suggestion = "Adding a lean protein source may make it more filling and balanced.";
  } else if (fiberDesc === "low in fibre") {
    suggestion = "Adding fibre-rich vegetables or whole grains may make it more balanced.";
  } else if (score >= 70) {
    suggestion = "This looks like a well-rounded plate — pairing it with water and a piece of fruit rounds it out nicely.";
  } else {
    suggestion = "Pairing it with vegetables and staying hydrated may make the overall day more balanced.";
  }

  const standPhrase = stand.length ? stand.join(", ") : "a mix of macros";
  return `This meal is estimated to be ${standPhrase}.${foodPhrase} contribute most of that. ${suggestion}`;
}

export default function NutritionScannerScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isPremium = !!user?.premium;
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);     // structured error envelope
  const [lastB64, setLastB64] = useState(null); // for Try Again
  const [saving, setSaving] = useState(false);  // explicit-save in flight

  // App-Store-safe premium disclaimer + sources modal (see NutritionInfoModal).
  // `null` closed; "disclaimer" or "sources" when open.
  const [infoModal, setInfoModal] = useState(null);

  // Daily tracker state — re-fetched after every save / delete so the bars
  // and Today's Meals list stay in sync with the database.
  const [today, setToday] = useState(null);
  const [openMeal, setOpenMeal] = useState(null);

  const refreshToday = async () => {
    try {
      const { data } = await api.get("/nutrition/today");
      setToday(data);
    } catch (_e) { /* leave previous state */ }
  };

  useEffect(() => {
    api.get("/nutrition/scans")
      .then((r) => setHistory(r.data?.scans || []))
      .catch(() => setHistory([]));
    refreshToday();
  }, []);

  // Auto-rollover at the user's local midnight — re-fetch /today so the
  // bars reset to 0 / 2,500 kcal for the new day without a page refresh.
  useEffect(() => {
    let timer;
    const schedule = () => {
      const now = new Date();
      const next = new Date(now);
      next.setHours(24, 0, 30, 0); // 30s after local midnight
      const ms = Math.max(60_000, next - now);
      timer = setTimeout(() => {
        refreshToday();
        schedule();
      }, ms);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  const onDeleteMeal = async (scanId) => {
    try {
      await api.delete(`/nutrition/scans/${scanId}`);
      setHistory((h) => h.filter((x) => x.id !== scanId));
      setOpenMeal(null);
      toast.show("Meal removed", "success");
      refreshToday();
    } catch {
      toast.show("Couldn't remove that meal", "error");
    }
  };

  const runScan = async (b64) => {
    setError(null);
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post(
        "/nutrition/scan",
        { image_base64: b64 },
        { timeout: 90_000 }
      );
      // New scans come back saved=false from the backend — they don't count
      // toward daily calories until the user explicitly taps "Save Meal".
      setResult(data);
      setHistory((h) => [
        { id: data.scan_id, scan: data, saved: data.saved !== false, created_at: data.created_at },
        ...h.filter((x) => x.id !== data.scan_id),
      ]);
      try { track("feature_completed", { feature: "nutrition", sub_action: "scan" }); } catch { /* ignore */ }
      toast.show("Scan complete", "success");
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally { setLoading(false); }
  };

  const saveScanToDailyLog = async () => {
    if (!result?.scan_id || result.saved || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/nutrition/scans/${result.scan_id}/save`);
      setResult((r) => r ? {
        ...r,
        saved: true,
        daily_progress: data.daily_progress || r.daily_progress,
      } : r);
      setHistory((h) => h.map((x) => x.id === result.scan_id ? { ...x, saved: true } : x));
      toast.show(
        data.already_saved ? "Already in your daily log" : "Added to your daily log",
        "success",
      );
      // Refresh the tracker so today's totals + meal-grouping reflect the
      // newly-saved meal without requiring the user to navigate away.
      refreshToday();
    } catch {
      toast.show("Couldn't save that meal. Try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const onPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    setResult(null);
    try {
      const dataUrl = await fileToResizedDataUrl(f, 1200);
      setPreview(dataUrl);
      const b64 = dataUrl.split(",", 2)[1];
      setLastB64(b64);
      await runScan(b64);
    } catch {
      setError({
        type: "upload_failure",
        retryable: false,
        message: "Couldn't read that file. Try a JPEG or PNG under 6 MB.",
      });
      setLoading(false);
    }
  };

  const onRetry = () => { if (lastB64) runScan(lastB64); };
  const onPickNew = () => fileRef.current?.click();

  const resetScan = () => {
    setResult(null);
    setPreview(null);
    setError("");
  };

  return (
    <div className="screen with-nav fade-in" data-testid="nutrition-scanner-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/home")} aria-label="Back" data-testid="nutri-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">NUTRITION SCAN</div>
        <button
          className="kbd-pill"
          onClick={() => navigate("/nutrition/targets")}
          data-testid="nutri-targets-btn"
          title="Open your daily calorie & macro targets"
        >
          Targets
        </button>
      </div>

      {/* Premium gate — Calorie Scanner is Premium-only. Free users see this
          message instead of the scanner. Copy per product spec. */}
      {!isPremium && (
        <div className="premium-gate" data-testid="nutrition-premium-gate">
          <div className="premium-gate-icon">
            <Lock size={22} />
          </div>
          <div className="premium-gate-title">
            <span role="img" aria-hidden="true">🔒</span> Unlock Your Nutrition Coach
          </div>
          <div className="premium-gate-body">
            Take a photo of your food and let IX NOVA estimate calories and help you stay on track.
            <br /><br />
            Upgrade to IX NOVA Premium to unlock the Calorie Scanner and take your progress even further.
          </div>
          <button
            type="button"
            className="btn btn-gold"
            onClick={() => navigate("/premium")}
            data-testid="nutrition-premium-cta"
            style={{ width: "100%", justifyContent: "center", marginTop: 6 }}
          >
            <Crown size={14} /> Unlock With Premium
          </button>
        </div>
      )}

      {isPremium && !result && !loading && (
        <>
          {/* Daily tracker — totals + macro bars + grouped meals. Renders at
              the TOP of the landing view so the user sees today's progress
              immediately, before any new scan. */}
          <DailyTracker
            today={today}
            onTapMeal={(meal) => setOpenMeal(meal)}
            onJumpHistory={() => navigate("/nutrition/targets")}
          />

          <div className="eyebrow" style={{ marginBottom: 8 }}>SCAN ANY MEAL</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.12 }}>
            Snap your plate.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Know what you&apos;re eating.</em>
          </h2>

          <NutritionDisclaimer compact />
          <p className="body" style={{ marginTop: 0, marginBottom: 22, color: "var(--text-dim)" }}>
            AI estimates calories, protein, carbs, fat, fibre, and tells you if it fits your goals. No profile required — adding one makes the verdicts personal.
          </p>

          <div className="photo-empty" data-testid="nutri-cta">
            <div className="photo-empty-icon"><Camera size={20} strokeWidth={1.8} /></div>
            <div className="body" style={{ color: "var(--text)", marginBottom: 6 }}>Take or upload a top-down photo</div>
            <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.10em" }}>Good light · full plate visible</div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", justifyContent: "center" }}>
              <button className="btn btn-gold" onClick={() => camRef.current?.click()} data-testid="nutri-camera-btn">
                <Camera size={14} /> Scan with camera
              </button>
              <button className="btn btn-outline" onClick={() => fileRef.current?.click()} data-testid="nutri-gallery-btn">
                <ImageIcon size={14} /> From gallery
              </button>
              <input ref={camRef}  type="file" accept="image/jpeg,image/png" capture="environment" hidden onChange={onPicked} data-testid="nutri-camera-input" />
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={onPicked} data-testid="nutri-gallery-input" />
            </div>
          </div>

          {error && (
            <AnalyzerErrorCard
              error={error}
              onRetry={lastB64 ? onRetry : undefined}
              onPickNew={onPickNew}
              feature="nutrition-scan"
              testId="nutri-error"
            />
          )}

          {history.length > 0 && (
            <div className="outfit-section" data-testid="nutri-history">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <History size={12} strokeWidth={1.8} /> RECENT SCANS
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.slice(0, 5).map((h) => {
                  const s = h.scan || {};
                  return (
                    <button
                      key={h.id}
                      type="button"
                      className="card"
                      style={{ padding: 14, display: "flex", gap: 12, alignItems: "center", textAlign: "left", width: "100%" }}
                      onClick={() => {
                        // Load the saved scan but preserve its saved-state so
                        // the "Save Meal" CTA reflects reality and we don't
                        // accidentally let the user re-save it (idempotent
                        // backend would catch it, but UX should match truth).
                        setResult({ ...s, scan_id: h.id, saved: h.saved !== false });
                        setPreview(null);
                      }}
                      data-testid={`nutri-history-${h.id}`}
                    >
                      <div className="tile-icon"><Flame size={15} strokeWidth={1.8} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="h3" style={{ color: "var(--text)", fontSize: 14 }}>
                          {(s.items || []).map((it) => it.name).slice(0, 2).join(", ") || "Meal"}
                        </div>
                        <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 3 }}>
                          {s.total_calories || "—"} kcal · score {(() => {
                            const raw = typeof s.health_score === "number" ? s.health_score : Number(s.health_score);
                            if (!raw && raw !== 0) return "—/100";
                            const n = raw <= 10 ? raw * 10 : raw;
                            return `${Math.max(0, Math.min(100, Math.round(n)))}/100`;
                          })()}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {isPremium && loading && (
        <div data-testid="nutri-loading" className="nutri-scan-stage">
          {/* Live scanning stage — the user's photo with a translucent overlay,
              animated corner brackets that lock onto the food, a sweep line
              that scans top-to-bottom, and a stack of live overlay chips
              (Calories, Protein, Confidence) that flash as the AI works. */}
          {preview && (
            <div className="nutri-scan-frame" data-testid="nutri-scan-frame">
              <img src={preview} alt="" className="nutri-scan-image" />
              <span className="nutri-scan-corner nutri-scan-corner-tl" />
              <span className="nutri-scan-corner nutri-scan-corner-tr" />
              <span className="nutri-scan-corner nutri-scan-corner-bl" />
              <span className="nutri-scan-corner nutri-scan-corner-br" />
              <div className="nutri-scan-sweep" data-testid="nutri-scan-sweep" />
              <div className="nutri-scan-grid" />
              <div className="nutri-scan-overlay-chips" data-testid="nutri-scan-overlay-chips">
                <div className="nutri-scan-chip"><span className="nutri-scan-chip-dot"/> CALORIES</div>
                <div className="nutri-scan-chip"><span className="nutri-scan-chip-dot"/> PROTEIN</div>
                <div className="nutri-scan-chip"><span className="nutri-scan-chip-dot"/> SCAN QUALITY</div>
              </div>
            </div>
          )}
          <div className="nutri-scan-status" data-testid="nutri-scan-status">
            <span className="nutri-scan-pulse" /> Analyzing your meal…
          </div>
          <AnalyzerProgress
            previewImg={null}
            totalSeconds={14}
            testId="nutri-loading-progress"
            steps={[
              "Uploading your meal photo securely…",
              "Identifying ingredients and portions…",
              "Estimating calories, protein, carbs, and fats…",
              "Checking against your goals & today's targets…",
              "Compiling your verdict and better-swap ideas…",
            ]}
          />
        </div>
      )}

      {isPremium && result && !loading && (
        <>
          <div className="top-bar" style={{ marginTop: -4, marginBottom: 8 }}>
            <div />
            <button className="icon-btn" onClick={resetScan} aria-label="Scan again" data-testid="nutri-scan-again">
              <X size={16} />
            </button>
          </div>

          {/* Elegant Apple-compliant disclaimer chip — small, premium, black &
              gold. Sits right below the results-title area (i.e. the top bar)
              so users see it before any nutrition numbers. Tapping "Learn
              more" opens NutritionInfoModal(mode="disclaimer"). Not a red
              banner — just a quiet, always-visible signal that these numbers
              are AI estimates, not medical advice. */}
          <div
            data-testid="nutri-disclaimer-chip"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              marginBottom: 16,
              borderRadius: 12,
              background: "rgba(178,138,62,0.06)",
              border: "1px solid rgba(178,138,62,0.22)",
            }}
          >
            <Info size={14} strokeWidth={1.8} color="var(--gold, #b28a3e)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 500, lineHeight: 1.3 }}>
                Nutrition Estimates
              </div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2, letterSpacing: "0.02em" }}>
                Estimates only · Not medical advice
              </div>
            </div>
            <button
              type="button"
              onClick={() => setInfoModal("disclaimer")}
              data-testid="nutri-disclaimer-learn-more"
              style={{
                background: "transparent",
                border: 0,
                color: "var(--gold, #b28a3e)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "4px 6px",
                flexShrink: 0,
              }}
            >
              Learn more <ChevronRight size={12} strokeWidth={2} />
            </button>
          </div>

          {preview && (
            <div className="outfit-section" data-testid="nutri-preview">
              <img src={preview} alt="" style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 18 }} />
            </div>
          )}

          {/* Per launch spec (2026-02-26): Nova Score removed from
              nutrition scan results. The page already has a Health Score —
              one number per meal is enough. The Nova Score continues to
              live on the Home dashboard and Progress page. */}

          {/* Per launch spec (2026-02-29): "TODAY VS YOUR GOAL" cards
              ("Calories today", "Protein today" with progress bars) removed
              from the scan results to keep this page focused on the single
              meal that was just scanned. Daily tracking (totals + progress
              bars + Today's Meals) lives in the DailyTracker on the
              Nutrition landing view and the Home dashboard. */}

          {/* Identified items — "Foods Detected" reads more naturally than "What We See" */}
          {result.items && result.items.length > 0 && (
            <div className="outfit-section" data-testid="nutri-items">
              <div className="eyebrow" style={{ marginBottom: 10 }}>FOODS DETECTED</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {result.items.map((it, i) => (
                  <span key={i} className="chip" style={{ pointerEvents: "none" }} data-testid={`nutri-item-${i}`}>
                    {it.name}{it.portion ? ` · ${it.portion}` : ""}
                  </span>
                ))}
              </div>
              {/* Scan quality — friendlier wording than "confidence". Low ≈ Estimate,
                  medium ≈ Good, high ≈ Very High. Hidden when missing. */}
              {result.confidence && (
                <div className="nutri-scan-quality" data-testid="nutri-scan-quality">
                  <span className="nutri-scan-quality-label">Scan quality</span>
                  <span className={"nutri-scan-quality-chip nutri-scan-quality-" + result.confidence}>
                    {result.confidence === "high" ? "Very High" :
                     result.confidence === "medium" ? "High" :
                     result.confidence === "low" ? "Medium" : "Estimate"}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Calorie hero — premium three-tier display: big estimate, tight
              range, and a confidence pill. Uses `calories_range` +
              `confidence` fields already returned by the analyzer. Range and
              confidence are always shown (not only for low/medium) so the
              user always sees the estimate honestly. */}
          <div
            className="glow-empty-hero"
            data-testid="nutri-calories"
            style={{ textAlign: "center", padding: "26px 22px" }}
          >
            <div className="tiny" style={{ letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--gold, #b28a3e)", fontSize: 10, fontWeight: 600 }}>
              Estimated Calories
            </div>
            <div style={{ marginTop: 14 }}>
              <div
                className="numeric"
                data-testid="nutri-calories-value"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 60,
                  color: "var(--text)",
                  lineHeight: 1,
                  letterSpacing: "-0.04em",
                }}
              >
                {typeof result.total_calories === "number"
                  ? result.total_calories.toLocaleString("en-US")
                  : result.total_calories}
              </div>
              <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.2em", textTransform: "uppercase", marginTop: 8, fontSize: 11 }}>
                kcal
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "center", flexWrap: "wrap" }}>
              {result.calories_range && (
                <div
                  data-testid="nutri-calories-range"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    minWidth: 148,
                  }}
                >
                  <div className="tiny" style={{ color: "var(--text-faint)", fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                    Estimated Range
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "var(--text)", fontWeight: 500 }}>
                    {(result.calories_range.min ?? 0).toLocaleString("en-US")}
                    –
                    {(result.calories_range.max ?? 0).toLocaleString("en-US")}
                    <span style={{ color: "var(--text-faint)", fontSize: 11, marginLeft: 4 }}>kcal</span>
                  </div>
                </div>
              )}
              {result.confidence && (
                <div
                  data-testid="nutri-confidence-pill"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: "rgba(178,138,62,0.08)",
                    border: "1px solid rgba(178,138,62,0.22)",
                    minWidth: 120,
                  }}
                >
                  <div className="tiny" style={{ color: "var(--text-faint)", fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                    Confidence
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "var(--gold, #b28a3e)", fontWeight: 500 }}>
                    {result.confidence === "high" ? "Very High" :
                     result.confidence === "medium" ? "High" :
                     result.confidence === "low" ? "Medium" : "Estimate"}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Macros */}
          {result.macros && (
            <div className="outfit-section" data-testid="nutri-macros">
              <div className="eyebrow" style={{ marginBottom: 12 }}>MACRONUTRIENTS</div>
              <div className="glow-sub-grid">
                <MacroTile icon={Beef}    label="Protein" value={result.macros.protein_g} unit="g" testId="nutri-protein" />
                <MacroTile icon={Wheat}   label="Carbs"   value={result.macros.carbs_g}   unit="g" testId="nutri-carbs" />
                <MacroTile icon={Droplet} label="Fat"     value={result.macros.fats_g}    unit="g" testId="nutri-fats" />
                {typeof result.macros.fiber_g === "number" && (
                  <MacroTile icon={Heart} label="Fibre" value={result.macros.fiber_g} unit="g" testId="nutri-fiber" />
                )}
              </div>
            </div>
          )}

          {/* Nutrition Balance (renamed from Health Score for Apple 1.4.1
              compliance — softer, non-clinical wording. The underlying value
              is the same server-side `health_score`). */}
          {typeof result.health_score === "number" && (
            <div className="outfit-section" data-testid="nutri-balance-section">
              <NutritionBalance score={result.health_score} />
            </div>
          )}

          {/* Goal support cards — reworked from binary Yes/No verdicts to
              Apple-safe guidance labels. Underlying `verdict.good` still
              drives icon tint but user-facing wording is always framed as an
              estimate ("May support…" / "May not support…"), never a
              diagnosis. */}
          {result.verdicts && (
            <div className="outfit-section" data-testid="nutri-verdicts">
              <div className="eyebrow" style={{ marginBottom: 12 }}>GOAL SUPPORT</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <GoalCard
                  label={result.verdicts.weight_loss?.good ? "May support weight-loss goals" : "May not support weight-loss goals"}
                  icon={TrendingDown}
                  verdict={result.verdicts.weight_loss}
                  testId="nutri-verdict-weight-loss"
                  onOpenSources={() => setInfoModal("sources")}
                />
                <GoalCard
                  label={result.verdicts.muscle_gain?.good ? "May support muscle-building goals" : "May not support muscle-building goals"}
                  icon={Dumbbell}
                  verdict={result.verdicts.muscle_gain}
                  testId="nutri-verdict-muscle-gain"
                  onOpenSources={() => setInfoModal("sources")}
                />
                <GoalCard
                  label={result.verdicts.energy?.good ? "May provide sustained energy" : "May provide short-term energy"}
                  icon={Zap}
                  verdict={result.verdicts.energy}
                  testId="nutri-verdict-energy"
                  onOpenSources={() => setInfoModal("sources")}
                />
              </div>
            </div>
          )}

          {/* Highlights + watch-outs — each item now carries a per-item
              citation tag. Old string-only shape is preserved via `textOf`
              and `citationOf` so historical scans still render correctly. */}
          {(result.highlights?.length || result.watch_outs?.length) && (
            <div className="outfit-section" data-testid="nutri-notes">
              {result.highlights?.length > 0 && (
                <>
                  <div className="eyebrow" style={{ marginBottom: 10 }}>HIGHLIGHTS</div>
                  <ul className="bullet-list" style={{ marginBottom: 14 }}>
                    {result.highlights.map((h, i) => (
                      <li key={i} data-testid={`nutri-highlight-${i}`}>
                        {textOf(h)}
                        <div>
                          <NutritionCitation
                            source={citationOf(h, "USDA")}
                            onOpenSources={() => setInfoModal("sources")}
                            testId={`nutri-highlight-${i}-cite`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {result.watch_outs?.length > 0 && (
                <>
                  <div className="eyebrow" style={{ marginBottom: 10 }}>WATCH OUTS</div>
                  <ul className="bullet-list">
                    {result.watch_outs.map((w, i) => (
                      <li key={i} data-testid={`nutri-watch-${i}`} style={{ color: "var(--text-dim)" }}>
                        {textOf(w)}
                        <div>
                          <NutritionCitation
                            source={citationOf(w, "WHO")}
                            onOpenSources={() => setInfoModal("sources")}
                            testId={`nutri-watch-${i}-cite`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Nutrition Summary — personalised recap based on the actual
              foods detected. Prefers the analyzer's `health_score_reason`
              (the server prompt now instructs the model to produce this
              directly). Falls back to `composePersonalisedSummary()`, which
              mines items + macros + score to build a natural 2-sentence
              educational summary referencing the specific foods. Never
              medical, always framed as an estimate. */}
          {(result.health_score_reason || result.items?.length || result.highlights?.length || result.watch_outs?.length) && (
            <div className="outfit-section" data-testid="nutri-summary">
              <div className="eyebrow" style={{ marginBottom: 10 }}>NUTRITION SUMMARY</div>
              <div
                className="card"
                style={{
                  padding: "16px 18px",
                  border: "1px solid rgba(178,138,62,0.18)",
                  background: "linear-gradient(180deg, rgba(178,138,62,0.05) 0%, rgba(0,0,0,0) 80%)",
                }}
              >
                <p
                  data-testid="nutri-summary-text"
                  style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: "var(--text-dim)" }}
                >
                  {composePersonalisedSummary(result)}
                </p>
                <NutritionCitation
                  source={citationOf({ source: result.summary_source }, "AND")}
                  onOpenSources={() => setInfoModal("sources")}
                  testId="nutri-summary-cite"
                />
              </div>
            </div>
          )}

          {/* Alternatives — personalised to the actually-detected foods.
              Server returns `{swap, for, benefit}` objects. Sentence form:
              "Replace the {swap} with {for}. {benefit}" — matches the exact
              copy pattern approved in App Review Notes. */}
          {result.alternatives?.length > 0 && (
            <div className="outfit-section" data-testid="nutri-alternatives">
              <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <ArrowLeftRight size={12} strokeWidth={1.8} /> BETTER ALTERNATIVES
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {result.alternatives.map((a, i) => (
                  <div
                    key={i}
                    className="card"
                    data-testid={`nutri-alt-${i}`}
                    style={{ padding: "14px 16px" }}
                  >
                    <div style={{ color: "var(--text)", fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>
                      Replace the <span style={{ color: "var(--gold, #b28a3e)" }}>{a.swap}</span>
                      {" "}with{" "}
                      <span style={{ color: "var(--gold, #b28a3e)" }}>{a.for}</span>.
                    </div>
                    {a.benefit && (
                      <div className="body-sm" style={{ color: "var(--text-dim)", marginTop: 6, lineHeight: 1.55 }}>
                        {a.benefit}
                      </div>
                    )}
                    <NutritionCitation
                      source={citationOf(a, "AND")}
                      onOpenSources={() => setInfoModal("sources")}
                      testId={`nutri-alt-${i}-cite`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sources & References — tappable pill that opens the modal
              instead of listing 5 long links inline. Sits directly above the
              Save Meal CTA per the design spec. */}
          <div className="outfit-section" data-testid="nutri-sources-section">
            <button
              type="button"
              onClick={() => setInfoModal("sources")}
              data-testid="nutri-sources-btn"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.06)",
                background: "rgba(255,255,255,0.03)",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: "rgba(178,138,62,0.10)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <BookOpen size={15} strokeWidth={1.8} color="var(--gold, #b28a3e)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 500 }}>
                  Sources & References
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 2 }}>
                  USDA · NIH · WHO · AND · PubMed
                </div>
              </div>
              <ChevronRight size={16} strokeWidth={1.7} color="var(--text-faint)" style={{ flexShrink: 0 }} />
            </button>
          </div>

          {/* Explicit save CTA — scanned meals do NOT count toward daily
              calories/macros/health score until the user taps this button.
              This prevents the "scan the same meal twice → ate 4000 cal"
              double-counting bug. Hidden once the meal is saved. */}
          {result.scan_id && !result.saved && (
            <button
              type="button"
              className="btn btn-gold"
              onClick={saveScanToDailyLog}
              disabled={saving}
              data-testid="nutri-save-meal-btn"
              style={{ width: "100%", justifyContent: "center", marginTop: 12, marginBottom: 4 }}
            >
              {saving ? (
                <>
                  <Sparkles size={14} /> Saving…
                </>
              ) : (
                <>
                  <Bookmark size={14} /> Save meal to daily log
                </>
              )}
            </button>
          )}

          {result.saved && (
            <div
              className="card"
              data-testid="nutri-saved-badge"
              style={{
                padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
                marginTop: 12, marginBottom: 4,
                border: "1px solid rgba(184,255,208,0.30)",
                background: "rgba(184,255,208,0.06)",
              }}
            >
              <BookmarkCheck size={16} color="var(--good)" strokeWidth={1.8} />
              <div className="body-sm" style={{ color: "var(--good)", fontWeight: 600 }}>
                Saved to your daily log
              </div>
              <div className="tiny" style={{ marginLeft: "auto", color: "var(--text-faint)" }}>
                Counted in today&apos;s totals
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button className="btn btn-gold" onClick={() => fileRef.current?.click()} data-testid="nutri-scan-new-btn" style={{ flex: 1, justifyContent: "center" }}>
              <RotateCw size={14} /> Scan another meal
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={onPicked} />
          </div>

          {/* Bottom spacer — makes sure the CTAs never disappear behind the
              app's bottom tab bar (z-index 999, ~76px tall) plus the iPhone
              home indicator safe area. */}
          <div
            aria-hidden="true"
            style={{ height: "calc(120px + env(safe-area-inset-bottom, 0px))" }}
          />
        </>
      )}

      {/* Tap-a-meal modal — shows items + remove-from-today CTA */}
      {openMeal && (
        <MealDetailModal
          meal={openMeal}
          onClose={() => setOpenMeal(null)}
          onDelete={onDeleteMeal}
        />
      )}

      {/* Premium disclaimer + sources bottom-sheet — App-Store 1.4.1
          compliance without the red banner. `infoModal` is one of
          null / "disclaimer" / "sources". */}
      <NutritionInfoModal
        open={!!infoModal}
        mode={infoModal || "disclaimer"}
        onClose={() => setInfoModal(null)}
      />
    </div>
  );
}
